import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { SCPagination } from 'rollup-allspark';
import HelpPopover from '../../../components/HelpPopover';
import {
  DEFAULT_PAGE_NO,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZES,
} from '../../../config';
import { cn, isEmpty } from '../../../utils';
import { readablePrice } from '../../../utils/getCurrency';
import { sortAggregatedDataRow } from '../../../utils/nativeAnalyticsUtils';

const ItemBreakDownTable = ({
  tableData,
  currentTab,
  itemBreakDownDataLoading,
}) => {
  // HOOKS
  const [isNameColumnHeaderIsOpen, setIsNameColumnHeaderIsOpen] =
    useState(false);
  const [isQuantityColumnHeaderIsOpen, setIsQuantityColumnHeaderIsOpen] =
    useState(false);
  const [isRevenueColumnHeaderIsOpen, setIsRevenueColumnHeaderIsOpen] =
    useState(false);
  const [tableFlattenedData, setTableFlattenedData] = useState([]);
  const [sortOrder, setSortOrder] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE_NO);
  const [currentSize, setCurrentSize] = useState(DEFAULT_PAGE_SIZE);
  const [finalTableRowData, setFinalTableRowData] = useState([]);

  const { currency, currency_symbol } = tableData || {};
  const currencyObj = { code: currency, symbol: currency_symbol };

  useEffect(() => {
    const flattenedData =
      tableData && tableData.analytics && tableData.analytics.length
        ? tableData.analytics
        : [];

    setTableFlattenedData(
      sortAggregatedDataRow(flattenedData, sortBy, sortOrder),
    );
  }, [tableData, sortOrder, sortBy, currentPage, currentSize]);

  useEffect(() => {
    const indexOfLastPerm = currentPage * currentSize;
    const indexOfFirstPerm = indexOfLastPerm - currentSize;

    if (tableFlattenedData && tableFlattenedData.length) {
      const tableRows = [];
      const tableRowsForCSV = [];
      tableFlattenedData.forEach(item => {
        const revenueCell = getRevenueCell(item);
        tableRows.push({
          title: item.title,
          quantity: item.quantity,
          revenue: revenueCell,
          currency,
        });
        tableRowsForCSV.push({
          title: item.title,
          quantity: item.quantity,
          revenue: revenueCell,
          revenue_cent: item.revenue,
          currency,
        });
      });
      setFinalTableRowData(tableRows.slice(indexOfFirstPerm, indexOfLastPerm));
    } else {
      setFinalTableRowData([]);
    }
  }, [tableFlattenedData, sortOrder, sortBy, currentPage, currentSize]);

  // HANDLERS
  const getRevenueCell = item => {
    const revenue = currency === 'USD' ? item.revenue_usd : item.revenue;
    return readablePrice(revenue, currencyObj);
  };
  const handleSorting = newSortBy => {
    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newSortOrder);
    setSortBy(newSortBy);
    setCurrentPage(DEFAULT_PAGE_NO);
  };

  const getSortClass = field => {
    let sortClass = 'kt-arrow-sort text-base';
    if (sortOrder === 'asc' && sortBy === field) {
      sortClass = 'kt-arrow-up';
    } else if (sortOrder === 'desc' && sortBy === field) {
      sortClass = 'kt-arrow-down';
    }
    return cn(sortClass, 'cursor-pointer');
  };

  const getRevenueKey = () => {
    if (tableData && tableData.currency === 'USD') {
      return 'revenue_usd';
    }
    return 'revenue';
  };
  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setCurrentSize(size);
  };
  // RENDER
  const pagination = !itemBreakDownDataLoading ? (
    <SCPagination
      className="mt-4"
      total={tableData && tableData.analytics ? tableData.analytics.length : 0}
      page={currentPage}
      size={currentSize}
      sizes={DEFAULT_PAGE_SIZES}
      onPageChange={handlePageChange}
    />
  ) : null;

  const renderNoDataRow = (
    <tr className="text-center">
      <td colSpan="11" className="p-5 text-sm text-grey-dark">
        No data found
      </td>
    </tr>
  );
  const tableHeaderBaseClass =
    'border-b border-grey p-3 text-xs font-semibold uppercase text-grey-darker';
  const tableHeaders = [
    `${currentTab && currentTab.title ? currentTab.title : ''} Name`,
    `Quantity Ordered`,
    'Revenue',
    'currency',
  ];
  const renderTableHeader = (
    <tr>
      <td className={cn(tableHeaderBaseClass, 'w-1/2')}>
        <div className="flex justify-between">
          <div className="flex">
            {tableHeaders[0]}
            <HelpPopover
              isPopoverOpen={isNameColumnHeaderIsOpen}
              position="right"
              isHtmlElement
              content={`Menu ${currentTab.title}`}
              setIsPopoverOpen={setIsNameColumnHeaderIsOpen}
              iconClassNames="sc-information cursor-pointer ml-1"
            />
          </div>
          <button
            type="button"
            className={getSortClass('title')}
            onClick={() => {
              handleSorting('title');
            }}
          >
            {/* button content */}
          </button>
        </div>
      </td>
      <td className={tableHeaderBaseClass}>
        <div className="flex justify-between">
          <div className="flex">
            {tableHeaders[1]}
            <HelpPopover
              isPopoverOpen={isQuantityColumnHeaderIsOpen}
              position="right"
              isHtmlElement
              content={`${currentTab.title} Quantity is the total number of times the menu ${currentTab.title} has been ordered from`}
              setIsPopoverOpen={setIsQuantityColumnHeaderIsOpen}
              iconClassNames="sc-information cursor-pointer ml-1"
            />
          </div>
          <button
            type="button"
            className={getSortClass('quantity')}
            onClick={() => {
              handleSorting('quantity');
            }}
          >
            {/* button content */}
          </button>
        </div>
      </td>
      <td className={tableHeaderBaseClass}>
        <div className="flex justify-between">
          <div className="flex">
            {tableHeaders[2]}
            <HelpPopover
              isPopoverOpen={isRevenueColumnHeaderIsOpen}
              position="right"
              isHtmlElement
              content={`The total gross sales for the ${currentTab.title}`}
              setIsPopoverOpen={setIsRevenueColumnHeaderIsOpen}
              iconClassNames="sc-information cursor-pointer ml-1"
            />
          </div>
          <button
            type="button"
            className={getSortClass(getRevenueKey())}
            onClick={() => {
              handleSorting(getRevenueKey());
            }}
          >
            {/* button content */}
          </button>
        </div>
      </td>
    </tr>
  );

  const renderTableRow = () => {
    if (isEmpty(finalTableRowData)) {
      return renderNoDataRow;
    }

    return finalTableRowData.map((item, index) => (
      <tr
        key={`${item.title}-${item.quantity}-${item.revenue}-${index + 1}`}
        className="hover:bg-grey-lighter"
      >
        <td className="px-3 py-3 border-b border-grey w-1/2">{item.title}</td>
        <td className="sc-tabular-nums px-3 py-3 border-b border-grey text-right">
          {item.quantity}
        </td>
        <td className="sc-tabular-nums px-3 py-3 border-b border-grey text-right">
          {item.revenue}
        </td>
      </tr>
    ));
  };

  return (
    <div className="bg-white w-full pb-4 rounded-b-lg">
      <div className="overflow-x-auto">
        <table className="table-auto w-full">
          <thead>{renderTableHeader}</thead>
          <tbody>{renderTableRow()}</tbody>
        </table>
      </div>
      {finalTableRowData && finalTableRowData.length ? pagination : null}
    </div>
  );
};

ItemBreakDownTable.propTypes = {
  tableData: PropTypes.any,
  currentTab: PropTypes.any,
  itemBreakDownDataLoading: PropTypes.bool,
};

export default ItemBreakDownTable;
